import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, ArrowLeft } from 'lucide-react';
import MissionControlNavRail from './MissionControlNavRail';

/**
 * Teaser — shared "Coming Soon / Beta" holding page that renders an
 * approved marketing artwork inside the Mission Control site shell.
 *
 * This is a VISUAL-only wrapper. It does not touch backend, wallet,
 * auth, or product logic — the two callers (`/mission-control/poker`
 * and `/mission-control/p2p-trade*`) are placeholder routes for
 * products that have not yet shipped.
 *
 * Props:
 *   imageSrc — public path to the approved poster (portrait
 *              preferred; component preserves aspect ratio).
 *   imageAlt — accessible alt text.
 *   accent   — subtle glow colour behind the artwork (Poker gold,
 *              P2P blue). Does NOT affect the artwork itself.
 *   testId   — root element data-testid.
 */
const Teaser = ({ imageSrc, imageAlt, accent = '#14F195', testId }) => {
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div
      className="min-h-screen bg-[#080B12] text-white overflow-x-hidden"
      style={{ fontFamily: 'Space Grotesk' }}
      data-testid={testId}
    >
      {/* Ambient depth — matches Mission Control */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(90,180,255,0.06),transparent_65%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(185,128,255,0.05),transparent_65%)]" />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[75vmin] h-[75vmin] rounded-full"
          style={{ background: `radial-gradient(closest-side, ${accent}12, transparent 70%)` }}
        />
      </div>

      {/* Shared Mission Control navigation drawer — single source of truth */}
      <MissionControlNavRail variant="overlay" open={navOpen} onClose={() => setNavOpen(false)} />

      {/* Top bar — mirrors Mission Control shell (unauth-safe subset) */}
      <header className="relative z-30 h-16 border-b border-white/8 bg-[#080B12]/85 backdrop-blur-md flex items-center px-4 md:px-6 gap-3">
        <button
          onClick={() => setNavOpen(v => !v)}
          data-testid="teaser-nav-toggle"
          aria-label="Toggle navigation"
          className="p-2 rounded-lg hover:bg-white/5 text-white/70 hover:text-white transition"
        >
          <Menu className="w-5 h-5" />
        </button>
        <Link to="/mission-control" className="flex items-center gap-3" data-testid="teaser-home-link">
          <img src="/brand/nexus-clean.png" alt="Degens DAO Nexus" className="w-8 h-8 object-contain" />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">Degens DAO</span>
            <span className="text-white text-sm" style={{ fontFamily: 'Oxanium', fontWeight: 700, letterSpacing: '0.05em' }}>
              Mission Control
            </span>
          </div>
        </Link>
        <div className="ml-auto">
          <Link
            to="/mission-control"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-white/25 bg-white/[0.02] hover:bg-white/[0.05] text-white/80 hover:text-white text-sm transition"
            style={{ fontFamily: 'Space Grotesk', fontWeight: 500 }}
            data-testid="teaser-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Mission Control</span>
          </Link>
        </div>
      </header>

      {/* Poster surface — centred, aspect-preserved, no crop */}
      <main
        className="relative z-10 flex items-center justify-center min-h-[calc(100vh-4rem)] px-3 py-4 md:py-6"
        data-testid="teaser-main"
      >
        <div
          className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_120px_rgba(0,0,0,0.65)]"
          style={{
            maxHeight: 'calc(100vh - 6rem)',
            maxWidth: 'min(560px, 100%)',
            boxShadow: `0 0 0 1px rgba(255,255,255,0.04), 0 30px 120px rgba(0,0,0,0.65), 0 0 80px ${accent}18`,
          }}
        >
          <img
            src={imageSrc}
            alt={imageAlt}
            draggable={false}
            className="block w-auto h-auto max-w-full select-none"
            style={{ maxHeight: 'calc(100vh - 6rem)' }}
            data-testid="teaser-artwork"
          />
        </div>
      </main>
    </div>
  );
};

export default Teaser;
